import { useMemo } from 'react';
import { UseFormReset, UseFormSetValue } from 'react-hook-form';

import { NotificationPosition } from 'core/enums';
import { FormProvider, useAgGridClientTableContext } from 'core/states';
import { useGetPartPricingMultiplier } from 'parts/hooks';
import { PricingHelper } from 'pricing/helpers';
import { useQuotePartActions } from 'quotes/hooks';
import { useSelectedQuotePartContext } from 'quotes/states';
import { ICalculatePnbPriceForm, ICustomerQuotePart, IQuotePart } from 'quotes/types';
import { getActiveQuoteParts } from 'quotes/utils';

import { mapQuotePartsCalculatePnbPricing } from './mappers';
import { QuotePartUpdatePnbMarginFormView } from './QuotePartUpdatePnbMarginFormView';
import { quotePartUpdateMargin } from './schema';
import { IBaseAdderMarginChangeValues, IBaseAdderPurchaseCostChangeValues } from './types';
import {
  calculatePricingByBaseAndAdder,
  formatCost,
  formatMargin,
  formatPrice,
  getDefaultValues,
  getFieldByType,
} from './utils';

interface IProps {
  isOpen: boolean;
  onClose: (closeSelection?: boolean) => void;
  onSubmitSuccess: () => void;
  interactive?: boolean;
}

export const QuotePartUpdatePnbMarginForm = ({
  isOpen = false,
  onClose,
  onSubmitSuccess,
  interactive = true,
}: IProps) => {
  const { rows, updateRows } = useAgGridClientTableContext();
  const { selectedQuotePart } = useSelectedQuotePartContext();

  const { pricingMultiplier } = useGetPartPricingMultiplier(
    {
      part: {
        number: selectedQuotePart?.partArr?.number,
        manufacturerId: selectedQuotePart?.partArr?.manufacturerId,
      },
    },
    { enabled: isOpen }
  );

  const { upsertQuoteParts } = useQuotePartActions({
    notificationPosition: NotificationPosition.FORM,
  });

  const activeRowData = getActiveQuoteParts(rows as IQuotePart[]);

  const multiplier = useMemo(() => pricingMultiplier?.standardCost, [pricingMultiplier]);
  const quotePartDecimals = selectedQuotePart?.quotePriceDecimals;
  const adderInfo = selectedQuotePart?.partArr?.adderInfo?.value;

  const onSubmit =
    (reset: UseFormReset<ICalculatePnbPriceForm>) => async (data: ICalculatePnbPriceForm) => {
      handleOnClose(reset, true);

      const { success, quoteParts } = await upsertQuoteParts(
        mapQuotePartsCalculatePnbPricing(
          data,
          selectedQuotePart as ICustomerQuotePart,
          activeRowData as ICustomerQuotePart[]
        ),
        { onSuccess: onSubmitSuccess }
      );

      if (!success) {
        return;
      }

      updateRows(
        { update: quoteParts },
        {
          refreshCells: true,
          redrawRows: true,
        }
      );
    };

  const onPurchaseCostChange = (
    purchaseCost: number,
    price: number,
    reset: UseFormReset<ICalculatePnbPriceForm>
  ) => {
    const cost = formatCost(purchaseCost, multiplier, quotePartDecimals);
    const margin = formatMargin(cost, price);

    reset({
      computedCost: cost,
      computedPurchaseCost: purchaseCost,
      price,
      margin,
    });
  };

  const onBaseAdderPurchaseCostChange = (
    {
      purchaseCostBase,
      purchaseCostAdder,
      costBase,
      costAdder,
      priceBase,
      priceAdder,
    }: IBaseAdderPurchaseCostChangeValues,
    setValue: UseFormSetValue<ICalculatePnbPriceForm>,
    type: string
  ) => {
    const costFieldUpdated =
      PricingHelper.PRICING_TYPE_BASE === type ? purchaseCostBase : purchaseCostAdder;
    const costFieldToUpdate = getFieldByType('computedCost', type);

    const costPnbUpdated = formatCost(costFieldUpdated, multiplier, quotePartDecimals);

    setValue(costFieldToUpdate, costPnbUpdated);

    const marginPnb = formatMargin(
      costPnbUpdated,
      PricingHelper.PRICING_TYPE_BASE === type ? priceBase : priceAdder
    );
    const marginFieldToUpdate = getFieldByType('margin', type);

    setValue(marginFieldToUpdate, marginPnb);

    if (!purchaseCostBase || !purchaseCostAdder) {
      setValue('computedCost', null);
      setValue('computedPurchaseCost', null);

      return;
    }

    const cost = calculatePricingByBaseAndAdder(
      type === PricingHelper.PRICING_TYPE_BASE ? costPnbUpdated : costBase,
      type === PricingHelper.PRICING_TYPE_BASE ? costAdder : costPnbUpdated,
      adderInfo,
      quotePartDecimals
    );

    setValue('computedCost', cost);

    const computedPurchaseCost = calculatePricingByBaseAndAdder(
      purchaseCostBase,
      purchaseCostAdder,
      adderInfo,
      quotePartDecimals
    );

    setValue('computedPurchaseCost', computedPurchaseCost);
  };

  const onMarginChange = (
    margin: number,
    cost: number,
    costBase: number,
    costAdder: number,
    setValue: UseFormSetValue<ICalculatePnbPriceForm>
  ) => {
    const price = formatPrice(cost, margin);

    setValue('price', price);

    setValue('marginBase', costBase ? margin : null);
    setValue('marginAdder', costAdder ? margin : null);

    const priceBase = formatPrice(costBase, margin);

    setValue('priceBase', priceBase);

    const priceAdder = formatPrice(costAdder, margin);

    setValue('priceAdder', priceAdder);
  };

  const onBaseAdderMarginChange = (
    {
      marginBase,
      marginAdder,
      costBase,
      costAdder,
      priceBase,
      priceAdder,
      cost,
    }: IBaseAdderMarginChangeValues,
    setValue: UseFormSetValue<ICalculatePnbPriceForm>,
    type: string
  ) => {
    const marginFieldUpdated = PricingHelper.PRICING_TYPE_BASE === type ? marginBase : marginAdder;
    const costField = PricingHelper.PRICING_TYPE_BASE === type ? costBase : costAdder;

    const priceFieldToUpdate = getFieldByType('price', type);

    const pricePnbUpdated = formatPrice(costField, marginFieldUpdated);

    setValue(priceFieldToUpdate, pricePnbUpdated);

    if (!marginBase || !marginAdder) {
      setValue('price', null);
      setValue('margin', null);

      return;
    }

    const price = calculatePricingByBaseAndAdder(
      type === PricingHelper.PRICING_TYPE_BASE ? pricePnbUpdated : priceBase,
      type === PricingHelper.PRICING_TYPE_ADDER ? pricePnbUpdated : priceAdder,
      adderInfo,
      quotePartDecimals
    );

    const margin = formatMargin(cost, price);

    setValue('price', price);
    setValue('margin', margin);
  };

  const onPriceChange = (
    price: number,
    cost: number,
    setValue: UseFormSetValue<ICalculatePnbPriceForm>,
    type = PricingHelper.PRICING_TYPE_TOTAL,
    priceBase = 0,
    priceAdder = 0
  ) => {
    const marginFieldToUpdate = getFieldByType('margin', type);

    const margin = formatMargin(cost, price);

    setValue(marginFieldToUpdate, margin);

    if (PricingHelper.PRICING_TYPE_TOTAL === type) {
      return;
    }

    const priceTotal = calculatePricingByBaseAndAdder(
      type === PricingHelper.PRICING_TYPE_BASE ? price : priceBase,
      type === PricingHelper.PRICING_TYPE_ADDER ? price : priceAdder,
      adderInfo,
      quotePartDecimals
    );

    const marginTotal = formatMargin(cost, priceTotal);

    setValue('price', priceTotal);
    setValue('margin', marginTotal);
  };

  const handleOnClose = (reset: UseFormReset<ICalculatePnbPriceForm>, closeSelection = false) => {
    reset();
    onClose(closeSelection);
  };

  const defaultValues = useMemo(
    () => getDefaultValues(selectedQuotePart as ICustomerQuotePart),
    [selectedQuotePart]
  );

  return (
    <FormProvider schema={quotePartUpdateMargin()} defaultValues={defaultValues} resetDefaultValues>
      <QuotePartUpdatePnbMarginFormView
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        selectedQuotePart={selectedQuotePart as ICustomerQuotePart}
        onPurchaseCostChange={onPurchaseCostChange}
        onBaseAdderPurchaseCostChange={onBaseAdderPurchaseCostChange}
        onPriceChange={onPriceChange}
        onMarginChange={onMarginChange}
        onBaseAdderMarginChange={onBaseAdderMarginChange}
        interactive={interactive}
      />
    </FormProvider>
  );
};
